import React, { useContext, useState } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import CloseIcon from '@mui/icons-material/Close';
import './EditorDrawer.scss';

// Section components
import { TypographySection } from './sections/TypographySection';
import { ShapeTypeSection } from './sections/ShapeTypeSection';
import { MediaSourceSection } from './sections/MediaSourceSection';
import { AppearanceSection } from './sections/AppearanceSection';
import { RemoveBackgroundSection } from './sections/RemoveBackgroundSection';
import { COMPONENT_SECTIONS_CONFIG } from './sections/sectionConfig';

/**
 * EditorDrawer - Side panel for editing component properties
 * Uses section-based architecture where each component type defines which sections to render
 */
export const EditorDrawer = ({
    isOpen,
    onClose,
    component,
    onContentChange,
    signDocId,
    sectionId,
    isTemplateMode = false,
    templateId
}) => {
    const { i18n } = useContext(I18nReactContext);

    // Special handling for background image and background video components
    const isBackgroundImage = component?.type === 'backgroundImage';
    const isBackgroundVideo = component?.type === 'backgroundVideo';
    const isBackgroundComponent = isBackgroundImage || isBackgroundVideo;

    // For background components, track which type is selected (image or video)
    const [backgroundType, setBackgroundType] = useState(component?.type === 'backgroundVideo' ? 'video' : 'image');

    // Get effective type for UI display (for background components, use backgroundType state)
    const effectiveType = isBackgroundComponent
        ? (backgroundType === 'image' ? 'image' : 'video')
        : (component?.type === 'image' ? 'image' : component?.type === 'video' ? 'video' : component?.type);

    // Get effective component type for i18n keys (backgroundImage, backgroundVideo, image, video)
    const effectiveComponentType = isBackgroundComponent
        ? (backgroundType === 'image' ? 'backgroundImage' : 'backgroundVideo')
        : component?.type;

    // Handle background type switching (image <-> video)
    const handleBackgroundTypeChange = async (newType) => {
        setBackgroundType(newType);

        // First remove the old background by setting it to null
        const oldComponentId = newType === 'image' ? 'background-video' : 'background-image';
        onContentChange(oldComponentId, null);

        // Then create the new background
        const newComponentId = newType === 'image' ? 'background-image' : 'background-video';

        // Set empty content for new background
        onContentChange(newComponentId, '', {
            opacity: component.opacity || 1,
            backgroundColor: component.backgroundColor || 'transparent',
            borderRadius: component.borderRadius || {
                topLeft: 0,
                topRight: 0,
                bottomLeft: 0,
                bottomRight: 0
            }
        });
    };

    // Get section configuration for current component type
    const getSectionConfig = () => {
        return COMPONENT_SECTIONS_CONFIG[component?.type] || { sections: [] };
    };

    // Render a section by name
    const renderSection = (sectionName) => {
        const config = getSectionConfig();
        const sectionConfig = config[sectionName] || {};

        const commonProps = {
            component,
            onContentChange,
        };

        switch (sectionName) {
            case 'typography':
                return <TypographySection key={sectionName} {...commonProps} />;

            case 'shapeType':
                return <ShapeTypeSection key={sectionName} {...commonProps} />;

            case 'mediaSource':
                return (
                    <MediaSourceSection
                        key={sectionName}
                        {...commonProps}
                        signDocId={signDocId}
                        sectionId={sectionId}
                        isTemplateMode={isTemplateMode}
                        templateId={templateId}
                        effectiveComponentType={effectiveComponentType}
                        effectiveType={effectiveType}
                        backgroundType={backgroundType}
                        onBackgroundTypeChange={handleBackgroundTypeChange}
                    />
                );

            case 'removeBackground':
                return (
                    <RemoveBackgroundSection
                        key={sectionName}
                        {...commonProps}
                        onClose={onClose}
                    />
                );

            case 'appearance':
                return (
                    <AppearanceSection
                        key={sectionName}
                        {...commonProps}
                        config={sectionConfig}
                    />
                );

            default:
                return null;
        }
    };

    if (!component) return null;

    const config = getSectionConfig();

    return (
        <div className={`EditorDrawer ${isOpen ? 'open' : 'closed'}`}>
            <div className="drawer-content">
                <div className="drawer-header">
                    <h3>{i18n.translate('weezbeeCourse.editor.editorDrawer.title', { defaultValue: 'Edit Component' })}</h3>
                    <button
                        className="close-button"
                        onClick={onClose}
                        title={i18n.translate('weezbeeCourse.editor.editorDrawer.closeButton', { defaultValue: 'Close' })}
                    >
                        <CloseIcon fontSize="small" />
                    </button>
                </div>
                <div className="drawer-sections">
                    {config.sections.map(sectionName => renderSection(sectionName))}
                </div>
            </div>
        </div>
    );
};
